import { Vector2 } from "three";
import { ComparableProperty } from "../../events/ComparableProperty";
import { Dimensions } from "../basics/Dimensions";
import { GraphicalElement } from "../graphical/GraphicalElement";
import { UIComponent } from "./UIComponent";
import { Property } from "../../events/Property";
import { Range } from "../../geometry/Range";
import { MathX } from "../../math/MathX";
import { ElementType } from "../../dom-tools/ElementType";
import { Events, OnKeyDown, OnMouseDown, OnMouseDrag, OnMouseMove, OnMouseUp } from "../../dom-tools/EventListeners";
import { EventSlot } from "../../events/EventSlot";
import { LinkedGraphicalElement } from "../graphical/LinkedGraphicalElement";
import { DOMHitTest } from "../../dom-tools/DOMHitTest";
import { Rectangle } from "../graphical/Rectangle";
import { ClassFlag } from "../../dom-tools/ClassFlag";
import { UIProperty } from "../basics/UIProperty";
import { DirectionFlags } from "../basics/DirectionFlags";
import { CustomMouse } from "../../dom/CustomMouse";
import { UICursors } from "../basics/UICursors";

export class ScrollBar extends UIComponent
{
  static readonly elementType = new ElementType( "ui--scrollbar" );

  

  readonly dimensions = new Dimensions();

  static readonly backgroundFlag = new ClassFlag( "ui--background" );
  readonly background = new LinkedGraphicalElement( this, ScrollBar.backgroundFlag );

  static readonly sliderFlag = new ClassFlag( "ui--slider" );
  readonly slider = new LinkedGraphicalElement( this, ScrollBar.sliderFlag );

  readonly isHorizontal = new UIProperty<boolean>( this, true );

  readonly offset = new UIProperty<number>( this, 0 );
  readonly zoom   = new UIProperty<number>( this, 0.5 );
  
  readonly sliderSizeMinZoom = new UIProperty<number>( this, 99 );
  readonly sliderSizeMaxZoom = new UIProperty<number>( this, 5 ); 

  readonly offsetSensitivity = new UIProperty<number>( this, 100 );
  readonly zoomSensitivity = new UIProperty<number>( this, 500 );

  readonly zoomEnabled = new UIProperty<boolean>( this, false );
  
  constructor()
  {    
    super();
    this._setElement( ScrollBar.elementType.create() );

    this.#addEvents();

    this.blockRedraw();

    this.background.value = new Rectangle();
    this.slider.value     = new Rectangle();
    
    this.slider.value.margins.value.all = 4;

    this.isHorizontal.forceDispatch();
    this.forceRedraw();
  }
  

  #addEvents()
  {
    this.isHorizontal.addListener(
      ()=>
      {
        let horizontal = this.isHorizontal.value;

        let removingFlag = horizontal ? DirectionFlags.vertical : DirectionFlags.horizontal;
        let addingFlag   = horizontal ? DirectionFlags.horizontal : DirectionFlags.vertical;  
        
        removingFlag.removeFrom( this.element );
        addingFlag.set( this.element );
      }
    );
    

    this._linkRedraw(
      this.isHorizontal,
      this.offset,
      this.zoom,
      this.sliderSizeMinZoom,
      this.sliderSizeMaxZoom
    )


    this.#addDragListeners();
    
  } 

  #slowDrag = false;
  #changeMode:(e:KeyboardEvent)=>void;
  #zooming = false;

  #addDragListeners()
  {
    this.slider.on( OnMouseDrag,
      ( e:MouseEvent )=>
      {
        if ( e.button !== 0 && e.button !== 1 )
        {
          return false;
        }


        ClassFlag.active.set( this.slider.value.element );
        
        let graphics = this.slider.value;
        let position = DOMHitTest.getPointerPosition( e );

        
        if ( Events.isStart( e ) )
        {
          this.#zooming = e.button === 1;

          if ( ! this.#changeMode )
          {
            this.#changeMode = (e:KeyboardEvent)=>{ if ( e.shiftKey ){ this.#slowDrag = !this.#slowDrag; } }
          }

          OnKeyDown.add( document.body, this.#changeMode );

          this.#slowDrag = e.shiftKey;

          graphics.setDragData( e, new Vector2( this.offset.value, this.zoom.value ) );

          UIComponent.lockCursor( UICursors.dragging +  "" );
        }

        let movement = position.sub( graphics.startPointerPosition );

        let offsetValue = this.isHorizontal.value ? movement.x : movement.y;
        let zoomValue   = this.isHorizontal.value ? movement.y : movement.x;
 
        let offsetChange = offsetValue / ( this.slidingRange * this.offsetSensitivity.value / 100 );
        let zoomChange   = zoomValue / ( this.zoomSensitivity.value );

        let dragData = graphics.getDragData<Vector2>();

        
        if ( this.#slowDrag )
        {
          offsetChange /= 10;
          zoomChange /= 10;
        }
        
        let startValue = dragData.value;

        let newOffsetValue = MathX.clamp01( startValue.x + offsetChange );
        let newZoomValue   = MathX.clamp01( startValue.y + zoomChange ); 

        
        
          this.offset.value = newOffsetValue;
               

        if ( this.zoomEnabled.value )
        {
          console.log( "CHANGE ZOOM", zoomChange, this.slidingRange, this.zoomSensitivity.value / 100 );
          this.zoom.value   = newZoomValue; 

          if ( isNaN( this.zoom.value ) )
          {
            this.zoom.value = 0;
          }
          
        }
        


        graphics.setDragData( e, new Vector2( this.offset.value, this.zoom.value ) );

        Events.silent( e );

        if ( Events.isEnd( e ) )
        {
          ClassFlag.active.removeFrom( this.slider.value.element );
          UIComponent.freeCursor();

          OnKeyDown.remove( document.body, this.#changeMode );
          
        }


      } 
    );    
  }

  get slidingSideLength()
  {
    return this.isHorizontal.value ? this.dimensions.width : this.dimensions.height;
  }

  get sliderSize()
  {
    let zoom   = this.zoom.value;

    let sliderMin = this.sliderSizeMinZoom.value;
    let sliderMax = this.sliderSizeMaxZoom.value;

    let sliderSizePercantage = MathX.lerp( sliderMin, sliderMax, zoom );

    return sliderSizePercantage / 100 * this.slidingSideLength;
  }

  get slidingRange()
  {    
    return this.slidingSideLength - this.sliderSize;
  }

  _update()
  {
    super._update();

    if ( this.background.value )
    {
      this.background.value.dimensions.set( 0, 0, this.dimensions.width, this.dimensions.height );
    }    

    let x = 0;
    let y = 0;
    let w = this.dimensions.width;
    let h = this.dimensions.height;
    
    if ( this.isHorizontal.value )
    {
      x = this.slidingRange * this.offset.value;       
      w = this.sliderSize;
    }
    else
    {
      y = this.slidingRange * this.offset.value;      
      h = this.sliderSize;
    }

    if ( this.slider.value )
    {
      this.slider.value.dimensions.set( x, y, w, h );
    }
    
  }
}